import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useFavorites } from '@/hooks/useFavorites';
import { getMediaByIds } from '@/utils/media';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/category/movie', label: '电影' },
  { to: '/category/tv', label: '电视剧' },
  { to: '/news', label: '资讯' },
  { to: '/special', label: '专题' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearchOpen(false);
    setMobileOpen(false);
    setFavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) {
      inputRef.current?.focus();
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword('');
    setSearchOpen(false);
  };

  const favoriteItems = getMediaByIds(favorites).slice(0, 5);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" aria-label="樱花视频首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-flower-fill text-xl"></i>
          </span>
          <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
            樱花<span className="text-primary-600">视频</span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <div className="relative hidden sm:block">
            <form
              onSubmit={submitSearch}
              className={`flex items-center overflow-hidden rounded-full border border-background-300 bg-background-100 transition-all duration-300 ${
                searchOpen ? 'w-56 px-3' : 'w-10 px-0'
              }`}
            >
              <button
                type={searchOpen ? 'submit' : 'button'}
                onClick={() => !searchOpen && setSearchOpen(true)}
                className="flex h-10 w-10 shrink-0 items-center justify-center text-foreground-600 hover:text-primary-600"
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              <input
                ref={inputRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、资讯"
                className={`h-10 min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 ${
                  searchOpen ? 'block' : 'hidden'
                }`}
              />
            </form>
          </div>

          <div className="relative">
            <button
              onClick={() => setFavOpen((v) => !v)}
              className="relative flex h-10 w-10 items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-accent-600"
              aria-label="我的收藏"
            >
              <i className="ri-heart-3-line text-lg"></i>
              {favorites.length > 0 && (
                <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-background-50">
                  {favorites.length}
                </span>
              )}
            </button>
            {favOpen && (
              <div className="absolute right-0 mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-2 animate-fade-in">
                <p className="px-2 py-1 text-xs font-medium text-foreground-500">我的收藏</p>
                {favoriteItems.length === 0 ? (
                  <p className="px-2 py-6 text-center text-sm text-foreground-400">
                    还没有收藏，去发现喜欢的影视吧
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {favoriteItems.map((item) => (
                      <Link
                        key={item.id}
                        to={`/detail/${item.id}`}
                        className="flex items-center gap-3 rounded-md p-2 hover:bg-background-100"
                      >
                        <span className="h-12 w-9 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img src={item.cover} alt={item.title} className="h-full w-full object-cover" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground-900">{item.title}</span>
                          <span className="text-xs text-foreground-500">
                            {item.type === 'movie' ? '电影' : '电视剧'} · {item.year}
                          </span>
                        </span>
                      </Link>
                    ))}
                    {favorites.length > 5 && (
                      <p className="px-2 py-1 text-center text-xs text-foreground-400">
                        共 {favorites.length} 部收藏
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 md:hidden"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 md:hidden animate-fade-in">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视、资讯"
              className="h-10 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="whitespace-nowrap text-sm font-medium text-primary-600">搜索</button>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-700'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}